import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import { LpiFilters, LpiAdvancedSelection } from './types';
import DateRangeDropdown from './DateRangeDropdown';
import MultiSelectDropdown from './MultiSelectDropdown';
import LpiAdvancedFilterDrawer from './LpiAdvancedFilterDrawer';
import './LpiFilterBar.scss';

// Format date in local timezone (YYYY-MM-DD)
const formatLocalDate = (date: Date | null): string => {
  if (!date) return '';
  return format(date, 'yyyy-MM-dd');
};

// Parse date string safely
const parseDateString = (dateStr: string): Date | null => {
  if (!dateStr) return null;
  return parseISO(dateStr);
};

interface LpiFilterBarProps {
  /** true once the bar has pinned to the top; drives the condensed styling */
  isStuck?: boolean;
  filters: LpiFilters;
  onFiltersChange: (filters: LpiFilters) => void;
  onApplyFilters: () => void;
  onClearFilters: () => void;
  clients: string[];
  vehicles: string[];
  vehicleTypes: string[];
  modalityDetails: string[];
  carriers: string[];
  isLoading: boolean;
  isLoadingClients?: boolean;
  isLoadingVehicles?: boolean;
  isLoadingVehicleTypes?: boolean;
  isLoadingModalityDetails?: boolean;
  isLoadingCarriers?: boolean;
  onClientsOpen?: () => void;
  onVehiclesOpen?: () => void;
  onVehicleTypesOpen?: () => void;
  onModalityDetailsOpen?: () => void;
  onCarriersOpen?: () => void;
}

const LpiFilterBar: FC<LpiFilterBarProps> = ({
  isStuck = false,
  filters,
  onFiltersChange,
  onApplyFilters,
  onClearFilters,
  clients,
  vehicles,
  vehicleTypes,
  modalityDetails,
  carriers,
  isLoading,
  isLoadingClients = false,
  isLoadingVehicles = false,
  isLoadingVehicleTypes = false,
  isLoadingModalityDetails = false,
  isLoadingCarriers = false,
  onClientsOpen,
  onVehiclesOpen,
  onVehicleTypesOpen,
  onModalityDetailsOpen,
  onCarriersOpen,
}) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Badge count: how many advanced values are active, so the button says so
  // without the user opening the drawer.
  const advancedCount =
    filters.vehicle_id.length + filters.modality_detail.length + filters.carrier_id.length;

  const advancedSelection: LpiAdvancedSelection = {
    vehicle_id: filters.vehicle_id,
    modality_detail: filters.modality_detail,
    carrier_id: filters.carrier_id,
  };

  const handleAdvancedApply = (next: LpiAdvancedSelection) => {
    onFiltersChange({ ...filters, ...next });
  };

  const handleAdvancedReset = () => {
    onFiltersChange({ ...filters, vehicle_id: [], modality_detail: [], carrier_id: [] });
  };


  const { t } = useTranslation();

  // Parse dates from string filters
  const startDate = parseDateString(filters.start_date);
  const endDate = parseDateString(filters.end_date);

  const handleStartDateChange = (date: Date | null) => {
    onFiltersChange({
      ...filters,
      start_date: formatLocalDate(date),
    });
  };

  const handleEndDateChange = (date: Date | null) => {
    onFiltersChange({
      ...filters,
      end_date: formatLocalDate(date),
    });
  };

  const handleClientsChange = (selected: string[]) => {
    onFiltersChange({ ...filters, client_id: selected });
  };

  const handleVehiclesChange = (selected: string[]) => {
    onFiltersChange({ ...filters, vehicle_id: selected });
  };

  // Vehicle Type filter temporarily replaced by Modality Detail (see below).
  // Kept for easy restoration.
  // const handleVehicleTypesChange = (selected: string[]) => {
  //   onFiltersChange({ ...filters, vehicle_type: selected });
  // };

  const handleModalityDetailsChange = (selected: string[]) => {
    onFiltersChange({ ...filters, modality_detail: selected });
  };

  const handleCarriersChange = (selected: string[]) => {
    onFiltersChange({ ...filters, carrier_id: selected });
  };

  // Transport order is the one filter with no options list to pick from —
  // orders are unbounded — so it is typed and committed with Enter, each
  // entry becoming a chip like the dropdown selections do.
  const [orderDraft, setOrderDraft] = useState('');

  const commitTransportOrder = () => {
    const value = orderDraft.trim();
    // Ignore blanks and duplicates: either would produce a chip that filters
    // nothing, and a duplicate would widen the IN list without widening the
    // result set.
    if (!value || filters.transport_order_number.indexOf(value) !== -1) {
      setOrderDraft('');
      return;
    }
    onFiltersChange({
      ...filters,
      transport_order_number: filters.transport_order_number.concat(value),
    });
    setOrderDraft('');
  };

  const removeFilter = (filterType: string, value?: string) => {
    const updatedFilters = { ...filters };

    switch (filterType) {
      case 'dateRange':
        updatedFilters.start_date = '';
        updatedFilters.end_date = '';
        break;
      case 'client':
        updatedFilters.client_id = updatedFilters.client_id.filter(
          (id) => id !== value
        );
        break;
      case 'vehicle':
        updatedFilters.vehicle_id = updatedFilters.vehicle_id.filter(
          (id) => id !== value
        );
        break;
      // Vehicle Type temporarily replaced by Modality Detail; kept for restoration.
      // case 'vehicleType':
      //   updatedFilters.vehicle_type = updatedFilters.vehicle_type.filter(
      //     (type) => type !== value
      //   );
      //   break;
      case 'modalityDetail':
        updatedFilters.modality_detail = updatedFilters.modality_detail.filter(
          (detail) => detail !== value
        );
        break;
      case 'carrier':
        updatedFilters.carrier_id = updatedFilters.carrier_id.filter(
          (id) => id !== value
        );
        break;
      case 'transportOrder':
        updatedFilters.transport_order_number =
          updatedFilters.transport_order_number.filter(
            (number) => number !== value
          );
        break;
      default:
        break;
    }

    onFiltersChange(updatedFilters);
  };

  const hasActiveFilters =
    filters.client_id.length > 0 ||
    filters.vehicle_id.length > 0 ||
    filters.vehicle_type.length > 0 ||
    filters.modality_detail.length > 0 ||
    filters.carrier_id.length > 0 ||
    filters.transport_order_number.length > 0 ||
    filters.start_date ||
    filters.end_date;

  return (

    <>
      <div className={`lpi-filter-bar${isStuck ? ' lpi-filter-bar--stuck' : ''}`}>
      {/* Filter Controls */}
      <div className="lpi-filter-bar__controls">
        {/* Date Range Dropdown - First */}
        <DateRangeDropdown
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={handleStartDateChange}
          onEndDateChange={handleEndDateChange}
          isLoading={isLoading}
        />

        {/* Client Filter */}
        <MultiSelectDropdown
          label={t('Client')}
          options={clients}
          selected={filters.client_id}
          onChange={handleClientsChange}
          placeholder={t('All Clients')}
          isLoading={isLoading || isLoadingClients}
          onOpen={onClientsOpen}
        />

        {/* Transport Order Filter */}
        <div className="lpi-filter-bar__text-filter">
          <label
            className="lpi-filter-bar__text-filter-label"
            htmlFor="lpi-transport-order-filter"
          >
            {t('Transport Order')}
          </label>
          <input
            id="lpi-transport-order-filter"
            className="lpi-filter-bar__text-filter-input"
            type="text"
            value={orderDraft}
            onChange={(e) => setOrderDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                // The bar sits inside no form, but Enter still reaches the
                // page; preventDefault keeps it from doing anything else.
                e.preventDefault();
                commitTransportOrder();
              }
            }}
            // Committing on blur too: typing a number and clicking Apply
            // directly is the obvious thing to do, and losing the entry
            // there would look like the filter is broken.
            onBlur={commitTransportOrder}
            placeholder={t('Order number')}
            disabled={isLoading}
          />
        </div>

        {/* Vehicle, Modality Detail and Carrier now live in the Advanced
            Filters drawer; the button below opens it and the pills under the
            bar show what is active. */}
        <div className="lpi-filter-bar__advanced">
          <span className="lpi-filter-bar__advanced-spacer">&nbsp;</span>
          <button
            type="button"
            className="btn btn-outline-primary lpi-filter-bar__advanced-btn"
            onClick={() => setIsDrawerOpen(true)}
            disabled={isLoading}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M1.5 2h13l-5 6v4.5L7.5 14V8L1.5 2z" stroke="currentColor"
                strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            {t('lpi.advancedFilters')}
            {advancedCount > 0 && (
              <span className="lpi-filter-bar__advanced-badge">{advancedCount}</span>
            )}
          </button>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="lpi-filter-bar__actions">
        <button
          className="btn btn-default"
          type="button"
          onClick={onClearFilters}
          disabled={isLoading}
        >
          {t('Clear')}
        </button>
        <button
          className="btn btn-primary"
          type="button"
          onClick={onApplyFilters}
          disabled={isLoading}
        >
          {t('Apply')}
        </button>
      </div>



    </div>

      {/* Active Filter Chips */}
      {hasActiveFilters && (
        <div className="lpi-filter-bar__chips">
          {/* Date Range Chip */}
          {(filters.start_date || filters.end_date) && (
            <span className="filter-chip">
              {filters.start_date || '...'} {t('to')}{' '}
              {filters.end_date || '...'}
              <button
                className="filter-chip__remove"
                type="button"
                onClick={() => removeFilter('dateRange')}
                aria-label={t('Remove date range filter')}
              >
                ✕
              </button>
            </span>
          )}

          {/* Client Chips */}
          {filters.client_id.map((client) => (
            <span key={client} className="filter-chip">
              {t('Client')}: {client}
              <button
                className="filter-chip__remove"
                type="button"
                onClick={() => removeFilter('client', client)}
                aria-label={`${t('Remove')} ${client}`}
              >
                ✕
              </button>
            </span>
          ))}

          {/* Vehicle Chips */}
          {filters.vehicle_id.map((vehicle) => (
            <span key={vehicle} className="filter-chip">
              {t('Vehicle')}: {vehicle}
              <button
                className="filter-chip__remove"
                type="button"
                onClick={() => removeFilter('vehicle', vehicle)}
                aria-label={`${t('Remove')} ${vehicle}`}
              >
                ✕
              </button>
            </span>
          ))}

          {/* Vehicle Type Chips — temporarily replaced by Modality Detail. */}
          {/* {filters.vehicle_type.map((vehicleType) => (
            <span key={vehicleType} className="filter-chip">
              {t('Vehicle Type')}: {vehicleType}
              <button
                className="filter-chip__remove"
                type="button"
                onClick={() => removeFilter('vehicleType', vehicleType)}
                aria-label={`${t('Remove')} ${vehicleType}`}
              >
                ✕
              </button>
            </span>
          ))} */}

          {/* Modality Detail Chips */}
          {filters.modality_detail.map((modalityDetail) => (
            <span key={modalityDetail} className="filter-chip">
              {t('Modality Detail')}: {modalityDetail}
              <button
                className="filter-chip__remove"
                type="button"
                onClick={() => removeFilter('modalityDetail', modalityDetail)}
                aria-label={`${t('Remove')} ${modalityDetail}`}
              >
                ✕
              </button>
            </span>
          ))}

          {/* Transport Order Chips */}
          {filters.transport_order_number.map((orderNumber) => (
            <span key={orderNumber} className="filter-chip">
              {t('Transport Order')}: {orderNumber}
              <button
                className="filter-chip__remove"
                type="button"
                onClick={() => removeFilter('transportOrder', orderNumber)}
                aria-label={`${t('Remove')} ${orderNumber}`}
              >
                ✕
              </button>
            </span>
          ))}

          {/* Carrier Chips */}
          {filters.carrier_id.map((carrier) => (
            <span key={carrier} className="filter-chip">
              {t('Carrier')}: {carrier}
              <button
                className="filter-chip__remove"
                type="button"
                onClick={() => removeFilter('carrier', carrier)}
                aria-label={`${t('Remove')} ${carrier}`}
              >
                ✕
              </button>
            </span>
          ))}
        </div>
      )}

      <LpiAdvancedFilterDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        onApply={handleAdvancedApply}
        onReset={handleAdvancedReset}
        filters={advancedSelection}
        vehicles={vehicles}
        modalityDetails={modalityDetails}
        carriers={carriers}
        isLoadingVehicles={isLoading || isLoadingVehicles}
        isLoadingModalityDetails={isLoading || isLoadingModalityDetails}
        isLoadingCarriers={isLoading || isLoadingCarriers}
        onVehiclesOpen={onVehiclesOpen}
        onModalityDetailsOpen={onModalityDetailsOpen}
        onCarriersOpen={onCarriersOpen}
      />
    </>
  );
};

export default LpiFilterBar;
